/**
 * Portfolio dashboard client — `GET /api/portfolio-dashboard`.
 */

import { oroborosJson, oroborosAuthHeaders } from './client';
import {
  interpretCloseAllApiResponse,
  interpretOroMutateApiResponse,
  interpretPositionCloseApiResponse,
  type CloseAllResultRow,
} from './tradeResponseQueue';
import { refreshOroborosSession } from './session';
import { oroPositionCoinKey, pairToHlCoin, pairToPerpLabel, resolveOroMarketId } from './portfolioPairs';
import {
  mapOroPosition,
  mergeHlDirectIntoDashboard,
  overlayNonHlOntoDashboard,
  carryPositionProtection,
  type OroExchangeBreakdown,
  type OroNonHlSlice,
  type OroPortfolioDashboard,
  type OroPosition,
  type OroVenueKey,
} from './portfolioMerge';
export {
  mergeHlDirectIntoDashboard,
  overlayNonHlOntoDashboard,
  carryPositionProtection,
  type OroExchangeBreakdown,
  type OroNonHlSlice,
  type OroPortfolioDashboard,
  type OroPosition,
  type OroVenueKey,
};
export { oroPositionCoinKey, pairToHlCoin, pairToPerpLabel, resolveOroMarketId };

export function oroVenueLabel(exchange: string): string {
  const s = String(exchange || '')
    .trim()
    .toLowerCase();
  if (s === 'hyperliquid' || s === 'hl') return 'Hyperliquid';
  if (s === 'propr') return 'Propr';
  if (!s || s === 'unknown') return '—';
  return String(exchange);
}

/** Strategy name/id when attributed; otherwise venue (Hyperliquid, Propr, …). */
export function oroPositionSourceLabel(position: Pick<
  OroPosition,
  'exchange' | 'sourceStrategyId' | 'sourceStrategyName'
>): string {
  const strategy =
    String(position.sourceStrategyName || '').trim() ||
    String(position.sourceStrategyId || '').trim();
  if (strategy) return strategy;
  return oroVenueLabel(String(position.exchange));
}

function normVenue(raw: unknown): OroVenueKey {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  if (s.includes('propr')) return 'propr';
  if (s.includes('hyper') || s === 'hl') return 'hyperliquid';
  return s || 'unknown';
}

function mapDashboard(raw: any): OroPortfolioDashboard {
  const breakdown = Array.isArray(raw?.exchangeBreakdown)
    ? raw.exchangeBreakdown.map((ex: any) => ({
        exchange: normVenue(ex?.exchange),
        positions: Number(ex?.positions || 0) || 0,
        unrealizedPnL: Number(ex?.unrealizedPnL || 0) || 0,
        marginUsed: Number(ex?.marginUsed || 0) || 0,
        accountValue: Number(ex?.accountValue || 0) || 0,
        totalEquity: Number(ex?.totalEquity || ex?.accountValue || 0) || undefined,
        availableEquity:
          ex?.availableEquity != null && Number.isFinite(Number(ex.availableEquity))
            ? Number(ex.availableEquity)
            : undefined,
        status: String(ex?.status || 'error'),
      }))
    : [];
  const positions = Array.isArray(raw?.summary?.positions)
    ? raw.summary.positions.map(mapOroPosition)
    : [];
  return {
    totalPortfolioValue: Number(raw?.totalPortfolioValue || 0) || 0,
    totalUnrealizedPnL: Number(raw?.totalUnrealizedPnL || 0) || 0,
    totalRealizedPnL: Number(raw?.totalRealizedPnL || 0) || 0,
    totalPositions: Number(raw?.totalPositions || positions.length || 0) || 0,
    loadState: raw?.loadState,
    exchangeBreakdown: breakdown,
    summary: {
      totalPositions: Number(raw?.summary?.totalPositions || positions.length || 0) || 0,
      totalUnrealizedPnL: Number(raw?.summary?.totalUnrealizedPnL || raw?.totalUnrealizedPnL || 0) || 0,
      totalMarginUsed: Number(raw?.summary?.totalMarginUsed || 0) || 0,
      positions,
    },
  };
}

export type OroPortfolioFetchResult = {
  success: boolean;
  dashboard?: OroPortfolioDashboard;
  rttMs: number;
  error?: string;
  unauthorized?: boolean;
  /** Main dashboard failed or degraded but some venue data is shown. */
  partial?: boolean;
  dashboardFailed?: boolean;
  sliceFailed?: boolean;
  warnings?: string[];
};

/** HL dashboard is down but the terminal can still show other venues / last-known state. */
export function isHyperliquidVenueOutage(message: string | null | undefined): boolean {
  const s = String(message || '').toLowerCase();
  return (
    s.includes('hyperliquid unavailable') ||
    s.includes('dashboard network error') ||
    s.includes('showing non-hl')
  );
}

function emptyPortfolioDashboard(): OroPortfolioDashboard {
  return mapDashboard({
    totalPortfolioValue: 0,
    totalUnrealizedPnL: 0,
    exchangeBreakdown: [],
    summary: { positions: [] },
    loadState: 'unavailable',
  });
}

function applyDashboardResponseMeta(
  dash: OroPortfolioDashboard,
  meta: { stale?: boolean; partial?: boolean },
): OroPortfolioDashboard {
  // SWR `stale` is cache age, not missing data — keep the snapshot loadState.
  if (meta.partial && dash.loadState !== 'partial' && dash.loadState !== 'unavailable') {
    return { ...dash, loadState: 'partial' };
  }
  return dash;
}

/** After hard 401 on non-HL, skip further slice calls until a successful session refresh. */
let nonHlAuthPaused = false;

export function resumeOroborosNonHlSliceFetch(): void {
  nonHlAuthPaused = false;
}

export async function fetchOroborosNonHlSlice(): Promise<{
  success: boolean;
  slice?: OroNonHlSlice;
  unauthorized?: boolean;
  error?: string;
}> {
  if (nonHlAuthPaused) {
    return { success: false, unauthorized: true, error: 'Unauthorized' };
  }

  const headers: Record<string, string> = {};

  const attempt = () =>
    oroborosJson<OroNonHlSlice>('/api/terminal/portfolio/non-hl-slice', { headers });

  let { ok, status, data } = await attempt();

  // Stale cookie / expired access: refresh once (same pattern as trade execute).
  if (status === 401 || status === 403) {
    const refreshed = await refreshOroborosSession();
    if (refreshed.success) {
      ({ ok, status, data } = await attempt());
    }
  }

  if (status === 401 || status === 403) {
    nonHlAuthPaused = true;
    return { success: false, unauthorized: true, error: 'Unauthorized' };
  }
  if (!ok || !data?.success) {
    return { success: false, error: (data as any)?.error || `Non-HL slice failed (${status})` };
  }
  return { success: true, slice: data };
}

export async function fetchOroborosPortfolioDashboard(opts?: {
  /** Bypass server in-memory SWR (use after close / trade mutations). */
  fresh?: boolean;
  /** Also fetch Propr/non-HL slice and overlay (default true). */
  includeNonHl?: boolean;
}): Promise<OroPortfolioFetchResult> {
  const t0 = performance.now();
  const qs = new URLSearchParams();
  try {
    const wallet =
      (window as any)?.JesterApp?.userWallet || localStorage.getItem('jester_user_wallet') || '';
    if (/^0x[a-fA-F0-9]{40}$/.test(wallet)) qs.set('wallet', wallet);
  } catch {
    /* ignore */
  }
  if (opts?.fresh) qs.set('fresh', '1');
  const path = `/api/portfolio-dashboard${qs.toString() ? `?${qs}` : ''}`;
  const headers: Record<string, string> = {};
  const includeNonHl = opts?.includeNonHl !== false;
  const slicePromise = includeNonHl
    ? fetchOroborosNonHlSlice().catch((e: any) => ({
        success: false as const,
        error: String(e?.message || 'Non-HL slice failed'),
      }))
    : Promise.resolve({ success: false as const });

  const attemptDashboard = () =>
    oroborosJson<{
      success?: boolean;
      dashboard?: any;
      error?: string;
      message?: string;
      stale?: boolean;
      partial?: boolean;
    }>(path, { headers });

  let dashRes = await attemptDashboard();
  if (dashRes.status === 401 || dashRes.status === 403) {
    const refreshed = await refreshOroborosSession();
    if (refreshed.success) {
      dashRes = await attemptDashboard();
    }
  }

  const { ok, status, data } = dashRes;
  const rttMs = Math.round(performance.now() - t0);
  const sliceRes = await slicePromise;
  const sliceFailed = includeNonHl && !sliceRes.success;
  const sliceOk = sliceRes.success && !!sliceRes.slice;

  const sliceOnlyResult = (reason: string): OroPortfolioFetchResult => {
    if (!sliceOk || !sliceRes.slice) {
      return { success: false, rttMs, error: reason, dashboardFailed: true, sliceFailed };
    }
    return {
      success: true,
      rttMs,
      partial: true,
      dashboardFailed: true,
      sliceFailed,
      warnings: [reason],
      dashboard: overlayNonHlOntoDashboard(emptyPortfolioDashboard(), sliceRes.slice),
    };
  };

  if (status === 401 || status === 403) {
    return { success: false, rttMs, unauthorized: true, error: 'Unauthorized', sliceFailed };
  }
  if (status === 0) {
    return sliceOnlyResult('Hyperliquid unavailable · Other venues remain operational');
  }
  if (!ok || !data?.success || !data.dashboard) {
    const err = data?.error || data?.message || `Dashboard failed (${status})`;
    if (sliceOk && sliceRes.slice) {
      return sliceOnlyResult(err);
    }
    return { success: false, rttMs, error: err, dashboardFailed: true, sliceFailed };
  }

  let dashboard = applyDashboardResponseMeta(mapDashboard(data.dashboard), {
    stale: !!data.stale,
    partial: !!data.partial,
  });
  if (sliceOk && sliceRes.slice) {
    dashboard = overlayNonHlOntoDashboard(dashboard, sliceRes.slice);
  }

  const partial =
    !!data.partial ||
    dashboard.loadState === 'partial' ||
    dashboard.loadState === 'unavailable' ||
    !!sliceRes.slice?.partial;
  const warnings: string[] = [];
  if (data.partial || dashboard.loadState === 'partial') {
    warnings.push('Dashboard load is partial');
  }
  if (sliceFailed && sliceRes.error) warnings.push(sliceRes.error);
  if (Array.isArray(sliceRes.slice?.venueErrors)) {
    for (const ve of sliceRes.slice.venueErrors) {
      const venue = String(ve?.venue || ve?.exchange || 'venue').trim();
      const msg = String(ve?.message || 'fetch failed').trim();
      if (venue) warnings.push(`${venue}: ${msg}`);
    }
  }

  return {
    success: true,
    rttMs,
    dashboard,
    partial: partial || undefined,
    sliceFailed: sliceFailed || undefined,
    warnings: warnings.length ? warnings : undefined,
  };
}

export type OroRiskMetrics = {
  riskState?: string;
  [key: string]: unknown;
};

export async function fetchOroborosRiskMetrics(): Promise<OroRiskMetrics | null> {
  try {
    const { ok, data } = await oroborosJson<any>('/api/terminal/risk-metrics', { method: 'GET' });
    if (!ok || !data) return null;
    return data?.metrics || data;
  } catch {
    return null;
  }
}

export function formatUsd(n: number, digits = 2): string {
  const abs = Math.abs(n);
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (n < 0) return `-$${body}`;
  return `$${body}`;
}

/** HL already flat — second close attempt after optimistic UI or WS lag. */
export function isPositionAlreadyClosedError(message: string | undefined | null): boolean {
  const msg = String(message || '').toLowerCase();
  return (
    msg.includes('reduce only') ||
    msg.includes('would increase position') ||
    msg.includes('no open position') ||
    msg.includes('position not found')
  );
}

export function formatSignedUsd(n: number, digits = 2): string {
  const abs = Math.abs(n);
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (n > 0) return `+$${body}`;
  if (n < 0) return `-$${body}`;
  return `$${body}`;
}

/**
 * Prefer MiniApp-style `hl:COIN` (or `hl:dex:COIN`) ids for close APIs that
 * expect them. Venue close also matches on `pair` via `hlPositionCoinKey`.
 */
function toClosePositionId(exchange: string, positionId: string, pair?: string): string {
  const id = String(positionId || '').trim();
  const venue = String(exchange || '')
    .trim()
    .toLowerCase();
  if (!(venue.includes('hyper') || venue === 'hl')) return id;
  if (/^hl:/i.test(id)) return id;

  const fromId = oroPositionCoinKey(id);
  const pairRaw = String(pair || '').trim();
  if (pairRaw.includes(':')) {
    const dexCoin = pairToHlCoin(pairRaw);
    if (dexCoin) return `hl:${dexCoin}`;
  }
  const coin = fromId || oroPositionCoinKey(pairRaw);
  return coin ? `hl:${coin}` : id;
}

/** Close an open position — terminal venue path (`POST /api/terminal/position/close`). */
export async function closeOroborosPosition(opts: {
  exchange: string;
  positionId: string;
  percentage?: number;
  pair?: string;
}): Promise<{ success: boolean; queued?: boolean; error?: string; unauthorized?: boolean }> {
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    queued?: boolean;
    duplicate?: boolean;
    error?: string;
    message?: string;
  }>('/api/terminal/position/close', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      exchange: opts.exchange,
      positionId: toClosePositionId(opts.exchange, opts.positionId, opts.pair),
      pair: opts.pair || undefined,
      percentage: opts.percentage ?? 100,
    }),
  });

  const interpreted = interpretPositionCloseApiResponse(ok, status, data);
  if (!interpreted.success) {
    return interpreted;
  }
  return { success: true };
}

/** Set open-position SL or TP by absolute price (MiniApp portfolio parity). */
export async function setOroborosPositionLevel(opts: {
  kind: 'sl' | 'tp';
  exchange: string;
  positionId: string;
  price: number;
  pair?: string;
}): Promise<{ success: boolean; error?: string; unauthorized?: boolean }> {
  const path =
    opts.kind === 'sl'
      ? '/api/miniapp/portfolio/position/sl/set'
      : '/api/miniapp/portfolio/position/tp/set';
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>(path, {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      exchange: opts.exchange,
      positionId: toClosePositionId(opts.exchange, opts.positionId, opts.pair),
      mode: 'price',
      price: opts.price,
    }),
  });

  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: true,
    fallbackError: `Failed to update ${opts.kind.toUpperCase()}`,
    networkError: `Network error — failed to update ${opts.kind.toUpperCase()}`,
  });
  if (!interpreted.success) return interpreted;
  return { success: true };
}

/** Remove open-position SL or TP (MiniApp portfolio parity). */
export async function removeOroborosPositionLevel(opts: {
  kind: 'sl' | 'tp';
  exchange: string;
  positionId: string;
  pair?: string;
}): Promise<{ success: boolean; error?: string; unauthorized?: boolean }> {
  const path =
    opts.kind === 'sl'
      ? '/api/miniapp/portfolio/position/sl/remove'
      : '/api/miniapp/portfolio/position/tp/remove';
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>(path, {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      exchange: opts.exchange,
      positionId: toClosePositionId(opts.exchange, opts.positionId, opts.pair),
    }),
  });

  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: true,
    fallbackError: `Failed to remove ${opts.kind.toUpperCase()}`,
    networkError: `Network error — failed to remove ${opts.kind.toUpperCase()}`,
  });
  if (!interpreted.success) return interpreted;
  return { success: true };
}

export type OroPositionStrategyHint = {
  sourceStrategyId?: string;
  sourceStrategyName?: string;
};

export async function fetchOroborosPositionStrategyHints(
  positions: Pick<OroPosition, 'pair' | 'side' | 'entryPrice'>[],
): Promise<Array<OroPositionStrategyHint | null>> {
  if (!positions.length) return [];
  const { ok, data } = await oroborosJson<{
    success?: boolean;
    hints?: Array<OroPositionStrategyHint | null>;
  }>('/api/miniapp/portfolio/positions/strategy-hints', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      positions: positions.map((p) => ({
        pair: p.pair,
        side: p.side === 'SHORT' ? 'short' : 'long',
        entryPrice: p.entryPrice,
      })),
    }),
  });
  if (!ok || !data?.success || !Array.isArray(data.hints)) {
    return positions.map(() => null);
  }
  return data.hints;
}

export function mergePositionStrategyHints(
  positions: OroPosition[],
  hints: Array<OroPositionStrategyHint | null>,
): OroPosition[] {
  return positions.map((p, i) => {
    const h = hints[i];
    if (!h?.sourceStrategyName && !h?.sourceStrategyId) return p;
    return {
      ...p,
      sourceStrategyId: h.sourceStrategyId,
      sourceStrategyName: h.sourceStrategyName,
    };
  });
}

/** Close all open positions (MiniApp portfolio parity). */
export async function closeAllOroborosPositions(): Promise<{
  success: boolean;
  error?: string;
  unauthorized?: boolean;
  message?: string;
  partial?: boolean;
}> {
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    partial?: boolean;
    error?: string;
    message?: string;
    results?: CloseAllResultRow[];
  }>('/api/miniapp/portfolio/positions/close-all', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({}),
  });

  const interpreted = interpretCloseAllApiResponse(ok, status, data);
  if (!interpreted.success) {
    return interpreted;
  }
  return {
    success: true,
    message: interpreted.message,
    partial: !!data?.partial,
  };
}
